/**
 * Validation of data that reaches the app through third-party software (BSI TR-03161-2 O.TrdP_7, O.Source_1):
 * Google Maps (geocoder results, place predictions), tokens that arrive in links or are injected by the
 * mobile apps, the claims of the SSO access token and deploy-time configuration.
 *
 * Nothing here trusts the shape of its input: every function accepts `unknown`, checks types and ranges,
 * strips control characters, limits lengths and returns either a safe value or `null` / an empty value.
 */

// ---------------------------------------------------------------------------------------------------------
// Primitive checks
// ---------------------------------------------------------------------------------------------------------

// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F-\u009F]/g;

/** A string without control characters, trimmed and cut to `maxLength`. Anything else becomes ''. */
export function safeText(value: unknown, maxLength = 200): string {
    return typeof value === 'string' ? value.replace(CONTROL_CHARACTERS, '').trim().slice(0, maxLength) : '';
}

export function isLatitude(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value) && value >= -90 && value <= 90;
}

export function isLongitude(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value) && value >= -180 && value <= 180;
}

export interface SafeLatLng {
    lat: number;
    lng: number;
}

/** Reads a position from a Google `LatLng` (lat()/lng() methods) or a literal ({lat, lng}); null when invalid. */
export function toLatLng(location: unknown): SafeLatLng | null {
    if (location === null || typeof location !== 'object') {
        return null;
    }
    const candidate = location as { lat?: unknown; lng?: unknown };
    try {
        const lat = typeof candidate.lat === 'function' ? candidate.lat() : candidate.lat;
        const lng = typeof candidate.lng === 'function' ? candidate.lng() : candidate.lng;
        return isLatitude(lat) && isLongitude(lng) ? { lat, lng } : null;
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------------------------------------------
// Google Maps: geocoder results and place predictions
// ---------------------------------------------------------------------------------------------------------

export interface SafeAddressComponent {
    types: string[];
    longName: string;
    shortName: string;
}

export interface SafeGeocodeResult {
    formattedAddress: string;
    position: SafeLatLng | null;
    components: SafeAddressComponent[];
}

export interface SafeAddressParts {
    zipCode: string;
    city: string;
    country: string;
    /** "route number", or the first address component when the result has no street number. */
    street: string;
    /** "zip city, country" with the parts that are known. */
    address: string;
}

/**
 * Validates one `google.maps.GeocoderResult`. Returns null when the result is missing or has no usable
 * content; malformed address components and positions are dropped instead of being passed on.
 */
export function parseGeocoderResult(result: unknown): SafeGeocodeResult | null {
    if (result === null || typeof result !== 'object') {
        return null;
    }
    const raw = result as { formatted_address?: unknown; address_components?: unknown; geometry?: { location?: unknown } };

    const components: SafeAddressComponent[] = [];
    if (Array.isArray(raw.address_components)) {
        for (const component of raw.address_components) {
            if (component === null || typeof component !== 'object') {
                continue;
            }
            const { types, long_name, short_name } = component as { types?: unknown; long_name?: unknown; short_name?: unknown };
            const safeTypes = Array.isArray(types) ? types.filter((type): type is string => typeof type === 'string').map((type) => safeText(type, 40)) : [];
            const longName = safeText(long_name);
            const shortName = safeText(short_name);
            if (safeTypes.length && (longName || shortName)) {
                components.push({ types: safeTypes, longName: longName || shortName, shortName: shortName || longName });
            }
        }
    }

    const formattedAddress = safeText(raw.formatted_address, 300);
    const position = toLatLng(raw.geometry?.location);
    if (!formattedAddress && !position && !components.length) {
        return null;
    }
    return { formattedAddress, position, components };
}

/** Validates the list returned by the geocoder and returns its first usable result. */
export function firstGeocoderResult(results: unknown): SafeGeocodeResult | null {
    return Array.isArray(results) && results.length ? parseGeocoderResult(results[0]) : null;
}

/** Zip code, city, country and street of a geocoder result, as plain safe strings ('' when unknown). */
export function addressParts(result: SafeGeocodeResult | null): SafeAddressParts {
    const component = (type: string) => result?.components.find((c) => c.types.includes(type))?.longName ?? '';
    const zipCode = component('postal_code');
    const city = component('locality') || component('sublocality');
    const country = component('country');
    const streetNumber = component('street_number');
    const street = streetNumber
        ? [component('route'), streetNumber].filter(Boolean).join(' ')
        : (result?.components[0]?.longName ?? '');
    const cityLine = [zipCode, city].filter(Boolean).join(' ');
    const address = [cityLine, country].filter(Boolean).join(', ');
    return { zipCode, city, country, street, address };
}

export interface SafePlacePrediction {
    description: string;
    place_id: string;
}

/** Keeps only the predictions that have a text and a place id; both are cleaned. */
export function safePlacePredictions(predictions: unknown): SafePlacePrediction[] {
    if (!Array.isArray(predictions)) {
        return [];
    }
    const result: SafePlacePrediction[] = [];
    for (const prediction of predictions.slice(0, 20)) {
        const { description, place_id } = (prediction ?? {}) as { description?: unknown; place_id?: unknown };
        const text = safeText(description, 300);
        if (text) {
            result.push({ description: text, place_id: safeText(place_id, 200) });
        }
    }
    return result;
}

/** Google Maps API keys consist of letters, digits, '-' and '_'. Used before the key goes into a script URL. */
export function isValidMapsApiKey(key: unknown): key is string {
    return typeof key === 'string' && /^[A-Za-z0-9_-]{20,100}$/.test(key);
}

// ---------------------------------------------------------------------------------------------------------
// Tokens from links, injected by the mobile apps or read from the URL
// ---------------------------------------------------------------------------------------------------------

/**
 * A token that is used as one URL path segment or query value must be a plain, bounded string:
 * no whitespace or control characters, nothing that changes the path or the query ('/', '\\', '?', '#',
 * '..') and a length limit. Everything else is left to the backend, which validates the token itself.
 */
export function isSafeToken(value: unknown): value is string {
    return isSafePathSegment(value, 1024) && value.length >= 2;
}

/**
 * A value that is placed into a URL path as one segment (a code or an id from a link or a route):
 * non-empty, bounded, no whitespace or control characters, nothing that changes the path or the query.
 */
export function isSafePathSegment(value: unknown, maxLength = 256): value is string {
    return (
        typeof value === 'string' &&
        value.length > 0 &&
        value.length <= maxLength &&
        // eslint-disable-next-line no-control-regex
        !/[\s\u0000-\u001F\u007F/\\?#]/.test(value) &&
        !value.includes('..')
    );
}

/** The value, if it is one of the allowed values (for example a language or a layout name). */
export function isAllowed<T extends string>(value: unknown, allowed: readonly T[]): value is T {
    return typeof value === 'string' && (allowed as readonly string[]).includes(value);
}

/** Name of a documentation page: letters, digits, '_' and '-' only. */
export function isSafeDocName(value: unknown): value is string {
    return typeof value === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(value);
}

/**
 * Removes control characters (and the C1 range). Line breaks and tabs stay when `keepLineBreaks` is true,
 * which is what multi-line fields need.
 */
export function stripControlCharacters(value: string, keepLineBreaks = false): string {
    // eslint-disable-next-line no-control-regex
    const pattern = keepLineBreaks ? /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g : CONTROL_CHARACTERS;
    return value.replace(pattern, '');
}

/**
 * Whether the app may send the browser to this address: a path of this app ('/...'), an address on this
 * origin, or an https address on one of the company's own domains. Everything else (other hosts,
 * 'javascript:' and 'data:' addresses, protocol-relative '//host') is refused.
 */
export function isTrustedRedirect(url: unknown, currentOrigin: string): url is string {
    // eslint-disable-next-line no-control-regex
    if (typeof url !== 'string' || url.length === 0 || url.length > 2048 || /[\s\u0000-\u001F\u007F]/.test(url)) {
        return false;
    }
    if (url.startsWith('/')) {
        return !url.startsWith('//') && !url.startsWith('/\\');
    }
    try {
        const parsed = new URL(url);
        if (parsed.username || parsed.password) {
            return false; // credentials in the address are a phishing trick
        }
        return parsed.origin === currentOrigin || (parsed.protocol === 'https:' && /(^|\.)almaphil\.(de|net)$/.test(parsed.hostname));
    } catch {
        return false;
    }
}

/** An id that arrives in a link and is used in an API path: digits only, at most 18 of them. */
export function isSafeId(value: unknown): value is string | number {
    return (typeof value === 'string' && /^\d{1,18}$/.test(value)) || (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0);
}

/** decodeURIComponent that does not throw on malformed input. */
export function safeDecodeURIComponent(value: string): string | null {
    try {
        return decodeURIComponent(value);
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------------------------------------------
// Tokens in links: query or fragment (BSI TR-03161-2 O.Source_11)
// ---------------------------------------------------------------------------------------------------------

/**
 * The value of the token `name` (`wst`, `eaa`, `eaf`) from the parameters of an address: a query string ("?eaa=...") or a
 * fragment ("#eaa=..."). Null when there is none or it is not well-formed.
 */
export function tokenFromParams(params: unknown, name: string): string | null {
    if (typeof params !== 'string') {
        return null;
    }
    for (const pair of params.replace(/^[?#]/, '').split('&')) {
        const separator = pair.indexOf('=');
        if (separator > 0 && pair.slice(0, separator) === name) {
            const token = safeDecodeURIComponent(pair.slice(separator + 1));
            return isSafeToken(token) ? token : null;
        }
    }
    return null;
}

/**
 * The token `name` of a whole address. The fragment is looked at first: links carry the token there, because a browser
 * never sends the fragment to a server. Links sent earlier carry it in the query.
 */
export function tokenFromUrl(url: unknown, name: string): string | null {
    if (typeof url !== 'string') {
        return null;
    }
    const hashAt = url.indexOf('#');
    const fragment = hashAt >= 0 ? url.slice(hashAt + 1) : '';
    const beforeFragment = hashAt >= 0 ? url.slice(0, hashAt) : url;
    const queryAt = beforeFragment.indexOf('?');
    const query = queryAt >= 0 ? beforeFragment.slice(queryAt + 1) : '';
    return tokenFromParams(fragment, name) ?? tokenFromParams(query, name);
}

/**
 * The same address with the token `name` in the fragment instead of the query, so that following it does not send the
 * token to any server. An address without that query parameter, or with a fragment already, is returned unchanged.
 */
export function tokenToFragment(url: string, name: string): string {
    try {
        const parsed = new URL(url);
        const token = parsed.searchParams.get(name);
        if (token === null || parsed.hash) {
            return url;
        }
        parsed.searchParams.delete(name);
        parsed.hash = `${name}=${encodeURIComponent(token)}`;
        return parsed.toString();
    } catch {
        return url;
    }
}

/**
 * The token `name` that a page kept in the state of its history entry (`history.state`) when it took the token off
 * the address bar. The state belongs to this tab and this entry only: it survives a reload, is never sent anywhere and
 * is not storage that other tabs can read.
 */
export function tokenFromHistoryState(state: unknown, name: string): string | null {
    if (typeof state !== 'object' || state === null) {
        return null;
    }
    const token = (state as Record<string, unknown>)[name];
    return isSafeToken(token) ? token : null;
}

/** The Wi-Fi invitation token (`wst`) from the parameters of an address. */
export function wifiTokenFromParams(params: unknown): string | null {
    return tokenFromParams(params, 'wst');
}

/** The Wi-Fi invitation token of a whole address. */
export function wifiTokenFromUrl(url: unknown): string | null {
    return tokenFromUrl(url, 'wst');
}

/** The Wi-Fi invitation address with the token in the fragment. */
export function wifiTokenToFragment(url: string): string {
    return tokenToFragment(url, 'wst');
}

// ---------------------------------------------------------------------------------------------------------
// The page to come back to after a sign-in
// ---------------------------------------------------------------------------------------------------------

/** Pages that are never a place to return to: the sign-in itself and the pages around it. */
const NO_RETURN_PAGE = /^\/(?:sign-in|sign-out|signed-in-redirect|404-not-found)(?:\/|$)/;
/** Parameters that are sign-in or access values and must not be kept with the address of a page. */
const NO_RETURN_PARAMS = ['code', 'state', 'session_state', 'iss', 'nonce', 'error', 'error_description', 'token', 'access_token', 'id_token', 'refresh_token', 'redirectURL', 'eaa', 'eaf', 'wst'];

/**
 * A page of this app that the user may be sent back to: a path with its query (never a fragment), without sign-in
 * or access values. Null for anything else, including an address of another site.
 */
export function safeReturnPage(value: unknown): string | null {
    if (!isTrustedRedirect(value, '') || !value.startsWith('/')) {
        return null;
    }
    try {
        const parsed = new URL(value, 'http://localhost');
        if (parsed.pathname === '/' || NO_RETURN_PAGE.test(parsed.pathname)) {
            return null;
        }
        NO_RETURN_PARAMS.forEach((name) => parsed.searchParams.delete(name));
        const query = parsed.searchParams.toString();
        return parsed.pathname + (query ? `?${query}` : '');
    } catch {
        return null;
    }
}

/**
 * The page the user is on, as the page to return to after a sign-in. On the sign-in page itself it is the page the
 * guard sent the user away from ("redirectURL"). Null when the user is on no page worth returning to.
 */
export function returnPageOf(location: { pathname: string; search: string }): string | null {
    if (location.pathname === '/sign-in') {
        return safeReturnPage(new URLSearchParams(location.search).get('redirectURL'));
    }
    return safeReturnPage(location.pathname + location.search);
}

// ---------------------------------------------------------------------------------------------------------
// SSO access token
// ---------------------------------------------------------------------------------------------------------

/**
 * The roles of an access token. A token that cannot be decoded, or a `roles` claim that is not a list of
 * strings, yields no roles instead of an exception or a wrong match.
 */
export function rolesFromToken(accessToken: unknown): string[] {
    if (typeof accessToken !== 'string') {
        return [];
    }
    try {
        const payload = accessToken.split('.')[1];
        if (!payload) {
            return [];
        }
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join(''),
        );
        const claims = JSON.parse(json) as { roles?: unknown };
        return Array.isArray(claims?.roles)
            ? claims.roles.filter((role): role is string => typeof role === 'string' && role.length <= 100)
            : [];
    } catch {
        return [];
    }
}
